import type { FlowVersionNode } from "@skaile/workspaces/resolver";
import type { FlowDefinition, FlowNode } from "./types.js";

/** Returns whether a node is executable by the currently active agent-turn runtime. */
export function isAgentNode(node: FlowNode): boolean {
  return node.type === "skill" || node.run?.kind === "agent";
}

/** Returns whether a node executes deterministically through NodeExecutor. */
export function isFunctionNode(
  node: FlowNode,
): node is FlowNode & { run: Extract<NonNullable<FlowNode["run"]>, { kind: "function" }> } {
  return node.run?.kind === "function";
}

/** Returns whether a node is a deterministic pass/fail verdict evaluated by the runtime. */
export function isCheckNode(
  node: FlowNode,
): node is FlowNode & { run: Extract<NonNullable<FlowNode["run"]>, { kind: "check" }> } {
  return node.run?.kind === "check";
}

/** Returns whether a node is a runtime-owned, isolated model call evaluated outside the session conversation. */
export function isSubpromptNode(
  node: FlowNode,
): node is FlowNode & { run: Extract<NonNullable<FlowNode["run"]>, { kind: "subprompt" }> } {
  return node.run?.kind === "subprompt";
}

/** Capitalized label for error/summary text — shared by every deterministic-node message site. */
export function deterministicNodeKindLabel(kind: "function" | "check"): string {
  return kind === "check" ? "Check" : "Function";
}

/** Returns whether a node is a runtime-owned human decision point. */
export function isGateNode(
  node: FlowNode,
): node is FlowNode & { run: Extract<NonNullable<FlowNode["run"]>, { kind: "gate" }> } {
  return node.run?.kind === "gate";
}

/** Returns whether a node deterministically selects one declared branch in the engine. */
export function isRouterNode(
  node: FlowNode,
): node is FlowNode & { run: Extract<NonNullable<FlowNode["run"]>, { kind: "router" }> } {
  return node.run?.kind === "router";
}

/** Returns whether a node delegates once to an inline child flow. */
export function isSubFlowNode(
  node: FlowNode,
): node is FlowNode & { run: Extract<NonNullable<FlowNode["run"]>, { kind: "sub-flow" }> } {
  return node.run?.kind === "sub-flow";
}

/** Returns whether the current runtime owns the node's execution state. */
export function isActiveRuntimeNode(node: FlowNode): boolean {
  return (
    isAgentNode(node) ||
    isFunctionNode(node) ||
    isCheckNode(node) ||
    isSubpromptNode(node) ||
    isGateNode(node) ||
    isRouterNode(node) ||
    isSubFlowNode(node)
  );
}

/** @deprecated Use {@link isActiveRuntimeNode}; gates are active but nobody executes them. */
export const isExecutableNode = isActiveRuntimeNode;

/** Returns the node's optional policy across strict v2 and legacy definitions. */
export function nodeIsOptional(node: FlowNode): boolean {
  return node.control?.optional ?? node.data?.optional ?? false;
}

/** Returns the node's mandatory approval policy across strict v2 and legacy definitions. */
export function nodeRequiresApproval(node: FlowNode): boolean {
  return node.gate?.approval === "mandatory" || node.data?.approval?.mandatory === true;
}

/** Returns the first declared skill requirement without making it the node identity. */
export function nodeSkillId(node: FlowNode): string {
  if (node.type === "skill") return node.data?.skill ?? node.id;
  const skillAsset = node.run?.assets?.find((asset) => asset.startsWith("skill:"));
  return skillAsset?.slice("skill:".length).replace(/#.*$/, "") ?? node.id;
}

/** Returns the user-facing node label across strict v2 and legacy definitions. */
export function nodeLabel(node: FlowNode): string {
  return node.label ?? node.data?.label ?? nodeSkillId(node);
}

/** Returns strict v2 run assets or the equivalent legacy declarations. */
export function nodeAssets(node: FlowNode): string[] {
  if (node.run?.assets) return node.run.assets;
  const legacyAssets = Array.isArray(node.data?.assets)
    ? node.data.assets.filter((asset): asset is string => typeof asset === "string")
    : [];
  const skill = node.type === "skill" && node.data?.skill ? `skill:${node.data.skill}` : undefined;
  return skill ? [skill, ...legacyAssets] : legacyAssets;
}

/**
 * Returns what executes the node, for display and grouping.
 *
 * Strict v2 `run.kind` wins, and every node reaching this through `loadFlow` has
 * one — normalization erases `type`. The legacy fallback only fires for a node
 * handed over un-normalized, so the result is not restricted to the strict v2
 * kind union. Never empty, so it is safe to render directly.
 *
 * A normalized legacy node reports the placeholder kind it became, not the kind
 * it was authored as; pair with {@link nodeIsInert} before showing it to a human.
 */
export function nodeRunKind(node: FlowNode): string {
  return node.run?.kind ?? node.type ?? "unknown";
}

/**
 * Returns whether the node is a permanently-blocked placeholder rather than a
 * step that can ever run.
 *
 * `normalizeV1` rewrites every non-executable legacy node — canvas `group`
 * containers, authored `gate`s, unknown kinds — into a router carrying
 * `contract.requires: [{ expr: "false" }]` and `control.optional: true`. Both are
 * normalization artifacts: the row is neither a routing step the author wrote nor
 * a step they marked skippable, so rendering either verbatim states something the
 * flow does not say. The false precondition is the surviving signal, and
 * permanent blocking is already its engine semantics.
 */
export function nodeIsInert(node: FlowNode): boolean {
  return node.contract?.requires?.some((r) => r.expr.trim() === "false") ?? false;
}

/**
 * Returns the node's first `skill:` asset reference, `#`-range included, or
 * `undefined` when the node requires no skill.
 *
 * Reads through {@link nodeAssets}, so a legacy `type: "skill"` node yields a
 * bare `skill:<id>` — the legacy pin lives in `data.version`, never in the ref.
 * Use {@link nodeSkillVersionPins} to read the pin itself.
 */
export function nodeSkillAsset(node: FlowNode): string | undefined {
  return nodeAssets(node).find((asset) => asset.startsWith("skill:"));
}

/**
 * Returns every skill-version pin the node declares, ready for `validateFlowVersions`.
 *
 * Strict v2 pins ride in `run.assets` as `skill:<identity>#<range>`; the legacy
 * `type: "skill"` + `data.version` shape is only consulted when no asset pin is
 * present. A skill requirement without a range is *not* a version claim and
 * yields nothing — returning it would make every unpinned node fail the check.
 *
 * Gotcha: refs are parsed by hand, not through `parseAssetRef`, which throws on
 * an unscoped ref like `skill:analyze#^9.0.0`. `skillId` is the segment after the
 * last `/`, so a scoped `skill:@pub/name#range` pins `name`.
 *
 * The legacy branch is unreachable for anything loaded through `loadFlow`, which
 * normalizes `type`/`data` away. `normalizeV1` can only rebuild a `skill:` ref
 * when the node names a skill, so a legacy node pinning `data.version` with no
 * `data.skill` declares no pin at all once normalized — the same thing `skaile
 * run` has always concluded for it.
 */
export function nodeSkillVersionPins(node: FlowNode): FlowVersionNode[] {
  const fromAssets = nodeAssets(node).flatMap((asset) => {
    if (!asset.startsWith("skill:")) return [];
    const hashIndex = asset.lastIndexOf("#");
    if (hashIndex <= "skill:".length || hashIndex === asset.length - 1) return [];
    const identity = asset.slice("skill:".length, hashIndex);
    return [
      {
        nodeId: node.id,
        skillId: identity.slice(identity.lastIndexOf("/") + 1),
        requestedVersion: asset.slice(hashIndex + 1),
      },
    ];
  });
  if (fromAssets.length > 0) return fromAssets;
  if (node.type !== "skill" || !node.data?.version) return [];
  return [
    {
      nodeId: node.id,
      skillId: node.data.skill ?? node.id,
      requestedVersion: node.data.version,
    },
  ];
}

/** Every skill-version pin declared across a flow, in node-definition order. */
export function flowSkillVersionPins(flow: FlowDefinition): FlowVersionNode[] {
  return flow.nodes.flatMap((node) => nodeSkillVersionPins(node));
}

/** Returns the binding values already supplied at the node boundary. */
export function nodeBindings(node: FlowNode): Record<string, unknown> {
  if (node.contract?.input) return node.contract.input;
  return node.data?.parameters ?? {};
}

/** Returns the first-class instruction for a strict v2 agent or subprompt node. */
export function nodeInstruction(node: FlowNode): string | undefined {
  return node.run?.kind === "agent" || node.run?.kind === "subprompt"
    ? node.run.instruction
    : undefined;
}

/** Returns run-scoped defaults across strict v2 and legacy definitions. */
export function flowDefaults(flow: FlowDefinition): Record<string, unknown> {
  return flow.defaults ?? flow.globals ?? {};
}
